import { Button, DatePicker, Flex, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import CustomDrawer from "../../../../components/ui/CustomDrawer";
import Loader from "../../../../components/ui/Loader";
import {
  useGetAllContractHistoryQuery,
  useRenewContractMutation,
} from "../../../../features/organization/contractual/JobDetails/jobDetailsApi";
import { useGetAllSalarySlabQuery } from "../../../../features/organization/contractual/SalarySlab/salarySlabApi";
import useError from "../../../../hooks/useError";
import { disablePastDates } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { validatePositiveNumber } from "../../../../utils/validators";

export default function ManageRenewContract({
  type,
  open,
  handleClose,
}: {
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const [form] = Form.useForm();
  const { id } = useParams();

  // ------ Queries ------
  const {
    data: allSalarySlabData,
    isLoading: allSalarySlabIsLoading,
    isError: allSalarySlabIsError,
    error: allSalarySlabError,
  } = useGetAllSalarySlabQuery({
    params: {
      page: 1,
      limit: 10000000000,
      status: "active",
    },
  });

  const {
    data: allContractHistoryData,
    isLoading: allContractHistoryIsLoading,
    isError: allContractHistoryIsError,
    error: allContractHistoryError,
  } = useGetAllContractHistoryQuery(
    {
      arg: {
        params: {
          page: 1,
          limit: 10000000000,
        },
      },
      id: id as unknown as number,
    },
    { skip: !id }
  );

  // ------ Mutations ------
  const [renewContract, { isLoading, isError, error, isSuccess }] =
    useRenewContractMutation();

  useError(isError, error, "Renew contract failed");
  useError(
    allContractHistoryIsError,
    allContractHistoryError,
    "Failed to fetch all contract history data"
  );
  useError(
    allSalarySlabIsError,
    allSalarySlabError,
    "Failed to fetch all salary slab data"
  );

  const firstContract = allContractHistoryData?.data?.contracts?.[0];
  const baseEndDate = firstContract?.contract_end_date;

  console.log("baseEndDate", baseEndDate);

  // When contract_months input changes
  const handleContractMonthsChange = (e: any) => {
    const value = e.target.value;
    const contractMonths = Number(value || 0);

    if (!value) {
      // Clear end date if input empty
      form.setFieldsValue({
        contract_end_date: null,
      });
      return;
    }

    if (baseEndDate && contractMonths > 0) {
      const newEndDate = dayjs(baseEndDate).add(contractMonths, "month");
      form.setFieldsValue({
        contract_end_date: dayjs(newEndDate),
      });
    }
  };

  const handleSlabChange = (value: any) => {
    if (!value) {
      // When cleared
      form.setFieldsValue({
        fixed_amount: undefined,
        variable_amount: undefined,
      });
      return;
    }

    const selectedSlab = allSalarySlabData?.data?.salarySlabs?.find(
      (item: any) => item.id === value
    );

    if (selectedSlab) {
      form.setFieldsValue({
        fixed_amount: selectedSlab.fixed_amount,
        variable_amount: selectedSlab.variable_amount,
      });
    }
  };

  const onFinish = (values: any) => {
    const newData = {
      applied_job_id: Number(id),
      salary_slab_id: values?.salary_slab_id,
      contract_end_date: dayjs(values?.contract_end_date).format("YYYY-MM-DD"),
      contract_months: Number(values?.contract_months),
      payment_type: values?.payment_type,
    };

    renewContract(newData);
  };

  // Initialize (if needed)
  useEffect(() => {
    if (firstContract && baseEndDate) {
      form.setFieldsValue({
        contract_end_date: dayjs(baseEndDate),
      });
    }
  }, [firstContract, baseEndDate, form]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Renew contract successfully");
      handleClose();
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <CustomDrawer
        width={460}
        open={open}
        onClose={handleClose}
        title={"Renew Contract"}
      >
        {allSalarySlabIsLoading || allContractHistoryIsLoading ? (
          <Loader />
        ) : (
          <Form
            form={form}
            size="large"
            layout="vertical"
            requiredMark="optional"
            className="custom-form"
            onFinish={onFinish}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="contract_months"
                label="Contract (Month)"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter contract month" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      // Prevent decimals
                      if (!/^[0-9]+$/.test(value)) {
                        return Promise.reject(
                          "Contract (Month) cannot be decimal"
                        );
                      }

                      // Prevent zero or negative
                      if (Number(value) <= 0) {
                        return Promise.reject(
                          "Contract (Month) cannot be negative or zero"
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="e.g. 5"
                  onChange={handleContractMonthsChange}
                  allowClear
                  type="number"
                />
              </Form.Item>

              <Form.Item
                name="contract_end_date"
                label="Contract End Date"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select contract end date",
                  },
                ]}
              >
                <DatePicker
                  className="w-full"
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  disabledDate={disablePastDates}
                  disabled={baseEndDate === undefined ? false : true}
                />
              </Form.Item>
            </div>
            <Form.Item
              name="salary_slab_id"
              label="Select Salary Slab"
              className="mb-4"
              rules={[{ required: true, message: "Please enter select slab" }]}
            >
              <Select
                placeholder="Select Slab"
                size="large"
                allowClear
                showSearch
                suffixIcon={<SelectDropdownLgIcon />}
                onChange={handleSlabChange}
                options={allSalarySlabData?.data?.salarySlabs?.map(
                  (item: any) => ({
                    label: item.slab_name,
                    value: item.id,
                  })
                )}
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="fixed_amount"
                label="Fixed Amount"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter fixed amount" },
                  { validator: validatePositiveNumber },
                ]}
              >
                <Input suffix="BDT" placeholder="0.00" disabled />
              </Form.Item>
              <Form.Item
                name="variable_amount"
                label="Variable Amount"
                className="mb-4"
                rules={[
                  { required: true, message: "Please enter variable amount" },
                  { validator: validatePositiveNumber },
                ]}
              >
                <Input suffix="BDT" placeholder="0.00" disabled />
              </Form.Item>
            </div>

            <Form.Item
              name="payment_type"
              label="Payment Type"
              className="mb-4"
              rules={[
                { required: true, message: "Please select payment type" },
              ]}
            >
              <Select
                placeholder="Select Payment Type"
                size="large"
                allowClear
                showSearch
                suffixIcon={<SelectDropdownLgIcon />}
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Weekly", value: "weekly" },
                  { label: "Daily", value: "daily" },
                ]}
              />
            </Form.Item>

            <Flex align="center" gap={16} className="mt-6">
              <Button
                size="middle"
                htmlType="button"
                className="min-w-[120px] h-10"
                onClick={() => handleClose()}
              >
                Cancel
              </Button>
              <Button
                size="middle"
                htmlType="submit"
                type="primary"
                color="primary"
                className="min-w-[120px] h-10"
                loading={isLoading}
              >
                Renew Contract
              </Button>
            </Flex>
          </Form>
        )}
      </CustomDrawer>
    </>
  );
}
